/**
 * ValuationSection Component
 *
 * Investor-facing valuation display for the score detail screen.
 * Shows the startup's computed valuation chart if available.
 * No feature gating — investors see whatever the startup computed.
 */

import React, { useMemo } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { BarChart3 } from 'lucide-react-native';
import { useQuery } from '@tanstack/react-query';

import { FootballFieldChart } from './FootballFieldChart';
import { ValuationDisclaimer } from './ValuationDisclaimer';
import { useThemeColors } from '@/lib/useThemeColors';
import { supabase } from '@/lib/supabase';
import { reportError } from '@/lib/errors';
import type { ValuationResult } from '@/lib/valuation/types';
import type { ValuationMethodBar } from './FootballFieldChart';

// ============================================================================
// Types
// ============================================================================

interface ValuationSectionProps {
  startupId: string;
  startup: {
    name?: string;
    stage?: string;
    industry?: string;
  };
}

// ============================================================================
// Method display config
// ============================================================================

const METHOD_LABELS: Record<string, string> = {
  berkus: 'Berkus Method',
  scorecard: 'Scorecard',
  'risk-factor': 'Risk Factor Sum.',
  'vc-method': 'VC Method',
};

// ============================================================================
// Helpers
// ============================================================================

function formatValuation(amount: number, currency: string = 'CHF'): string {
  if (amount >= 1_000_000_000)
    return `${currency} ${(amount / 1e9).toFixed(amount % 1e9 === 0 ? 0 : 1)}B`;
  if (amount >= 1_000_000)
    return `${currency} ${(amount / 1e6).toFixed(amount % 1e6 === 0 ? 0 : 1)}M`;
  if (amount >= 1_000)
    return `${currency} ${(amount / 1e3).toFixed(amount % 1e3 === 0 ? 0 : 1)}K`;
  return `${currency} ${amount.toFixed(0)}`;
}

// ============================================================================
// Component
// ============================================================================

export function ValuationSection({ startupId, startup }: ValuationSectionProps) {
  const colors = useThemeColors();

  // Fetch latest valuation for this startup
  const { data: valuation, isLoading } = useQuery({
    queryKey: ['valuation', 'latest', startupId],
    queryFn: async () => {
      // JUSTIFIED as any: 'valuation_results' table not in generated Supabase types yet
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('valuation_results')
        .select('*')
        .eq('startup_id', startupId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        reportError(error, { context: 'ValuationSection.fetchLatest', level: 'warning' });
        throw error;
      }
      return data as ValuationResult | null;
    },
    enabled: !!startupId,
    staleTime: 5 * 60 * 1000,
  });

  // Transform ValuationResult into chart bars (no gating for investors)
  const chartBars: ValuationMethodBar[] = useMemo(() => {
    if (!valuation?.methods) return [];

    return Object.entries(valuation.methods)
      .filter(([_, result]) => result.applicable)
      .map(([method, result]) => {
        const spreadFactor = 1 - result.confidence / 100;
        const spread = result.value * Math.max(spreadFactor * 0.4, 0.1);

        return {
          method,
          label: METHOD_LABELS[method] ?? method,
          min: Math.max(0, result.value - spread),
          max: result.value + spread,
          pointEstimate: result.value,
          confidence: result.confidence,
        };
      });
  }, [valuation]);

  const suggestedRange = valuation?.suggested_range;
  const currency = suggestedRange?.currency ?? 'CHF';

  // ── Loading State ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <View style={{ alignItems: 'center', paddingVertical: 24 }}>
        <ActivityIndicator size="small" color={colors.primary[500]} />
        <Text
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            marginTop: 8,
          }}
        >
          Loading valuation data...
        </Text>
      </View>
    );
  }

  // ── No Valuation ─────────────────────────────────────────────────
  if (!valuation) {
    return (
      <View
        style={{
          alignItems: 'center',
          paddingVertical: 24,
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: 24,
            backgroundColor: colors.surface.secondary,
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <BarChart3 size={22} color={colors.text.tertiary} />
        </View>
        <Text
          className="text-sm font-medium"
          style={{
            color: colors.text.secondary,
            textAlign: 'center',
          }}
        >
          No valuation computed yet.
        </Text>
        <Text
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            textAlign: 'center',
            marginTop: 4,
          }}
        >
          {startup.name ?? 'This startup'} has not run the valuation wizard.
        </Text>
      </View>
    );
  }

  // ── Has Valuation ────────────────────────────────────────────────
  return (
    <View>
      {/* Chart */}
      {chartBars.length > 0 && (
        <View style={{ marginBottom: 16 }}>
          <FootballFieldChart
            methods={chartBars}
            suggestedRange={suggestedRange ? {
              min: suggestedRange.min,
              max: suggestedRange.max,
              median: suggestedRange.median,
            } : undefined}
            animated={true}
            currency={currency}
          />
        </View>
      )}

      {/* Suggested range summary */}
      {suggestedRange && suggestedRange.max > 0 && (
        <View
          style={{
            backgroundColor: colors.surface.secondary,
            borderRadius: 12,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <Text
            className="text-xs font-semibold"
            style={{
              color: colors.text.tertiary,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            Suggested Range
          </Text>
          <Text
            className="text-lg font-bold"
            style={{
              color: colors.text.primary,
            }}
          >
            {formatValuation(suggestedRange.min, currency)} – {formatValuation(suggestedRange.max, currency)}
          </Text>
        </View>
      )}

      {/* Compact Disclaimer */}
      <ValuationDisclaimer
        variant="compact"
      />
    </View>
  );
}
